import { PlusIcon } from "@xola/icons";
import { Switch, Button as UIButton } from "@xola/ui-kit";
import { Loader } from "common";
import React, { useMemo, useState } from "react";
import { useIsFetching } from "react-query";
import { Button, ButtonType, Experience, ExperienceStatus, PopupCheckoutType, Product } from "types";
import { useDebounce } from "use-debounce";
import { ButtonCodeHeading } from "../ButtonCodeHeading";
import { MultipleItemCheckoutModal } from "../MultipleItemCheckoutModal/MultipleItemCheckoutModal";
import { ButtonCodeTable } from "./ButtonCodeTable";
import { SearchInput } from "@/components/Common/SearchInput";
import { isMongoID } from "@/helpers/validationHelper";
import { useProducts } from "@/hooks/resources/useProduct";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { useInfiniteCollection } from "@/hooks/useInfiniteCollection";
import { useModal } from "@/hooks/useVisibility";

interface NewStepFourButtonCodeSectionProps {
    popupType: PopupCheckoutType;
}

export const NewStepFourButtonCodeSection = ({ popupType }: NewStepFourButtonCodeSectionProps) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch] = useDebounce(searchTerm, 1000);
    const [showSystemButtons, setShowSystemButtons] = useState(false);
    const [selectedEditButton, setSelectedEditButton] = useState<Button>();
    const buttonModal = useModal(false);

    const handleOpenModal = (button: Button) => {
        setSelectedEditButton(button);
        buttonModal.show();
    };

    const handleCloseModal = () => {
        setSelectedEditButton(undefined);
        buttonModal.hide();
    };

    // Pre-fetch buttons for all types so they are ready when the user clicks on the tab. Otherwise feels slow
    const isFetching = useIsFetching(["buttons"]);
    const { data: allButtons } = useAutoFetchCollection<Button>("buttons");

    const buttons = useMemo(() => {
        return allButtons.filter((button) => {
            const isArchived = button.archivedAt;
            if (debouncedSearch.length > 0) {
                const nameMatches = button.name.toLowerCase().includes(debouncedSearch.toLowerCase());
                const idMatches = button.id.toString() === debouncedSearch;
                return (nameMatches || idMatches) && !isArchived;
            }
            return !isArchived;
        });
    }, [allButtons, debouncedSearch]);

    const canSearch = debouncedSearch.length > 0;
    const { products: allProducts } = useProducts();
    const fetchParams = {
        "object[in]": "experience,package,combo,membership,partner_package",
        status: ExperienceStatus.PUBLISHED,
        ...(canSearch ? { "name[contains]": debouncedSearch } : null),
    };
    const { data: products } = useInfiniteCollection<Product>("products", fetchParams);

    const { data: partnerExperiences } = useInfiniteCollection<Experience>("experiences", {
        partnerExperiences: true,
        ...(canSearch ? { "name[contains]": debouncedSearch } : null),
    });

    const filteredProducts = useMemo(() => {
        return isMongoID(debouncedSearch)
            ? allProducts.filter((product) => product.id === debouncedSearch)
            : [...products, ...partnerExperiences];
    }, [allProducts, products, partnerExperiences, debouncedSearch]);

    return (
        <div className="space-y-6 py-8">
            <ButtonCodeHeading number={3}>Copy + Paste button code</ButtonCodeHeading>
            <SearchInput value={searchTerm} onChange={(searchResult: string) => setSearchTerm(searchResult.trim())} />
            <div className="flex items-center justify-between">
                <Switch.Group>
                    <Switch isChecked={showSystemButtons} onChange={setShowSystemButtons} />
                    <Switch.Label direction="right" className="text-gray-darker">
                        Show system generated buttons
                    </Switch.Label>
                </Switch.Group>
                <UIButton icon={<PlusIcon />} onClick={() => buttonModal.show()}>
                    Create New Button
                </UIButton>
            </div>
            {isFetching && buttons.length === 0 ? (
                <Loader />
            ) : (
                <ButtonCodeTable
                    isSearch={canSearch}
                    buttons={buttons}
                    products={filteredProducts}
                    showSystemButtons={showSystemButtons}
                    popupType={popupType}
                    onEditButton={handleOpenModal}
                />
            )}
            <MultipleItemCheckoutModal
                isOpen={buttonModal.isVisible}
                selectedEditButton={selectedEditButton}
                buttonType={selectedEditButton?.type ?? ButtonType.CHECKOUT}
                onClose={handleCloseModal}
            />
        </div>
    );
};
